// Controls scheduled DTC promotion pricing in the browser so Shopify's cached
// Liquid HTML never decides whether a promotion is active from server time.
// Start/end values are interpreted as wall-clock times in America/Los_Angeles,
// regardless of the visitor's device timezone. This file also reapplies the
// discount after a PDP variant change.

(function () {
  if (!window.theme || !window.theme.settings) return;

  var settings = window.theme.settings;
  var defaultPct = parseFloat(settings.siteDiscountPercentage) || 0;
  var startAt = String(settings.siteDiscountStartAt || '').trim();
  var endAt = String(settings.siteDiscountEndAt || '').trim();
  var boundaryTimer = null;
  var mutationRefreshQueued = false;
  var maxTimerDelay = 2147483647;
  var pacificFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });

  function getPacificParts(timestamp) {
    var values = {};
    pacificFormatter.formatToParts(new Date(timestamp)).forEach(function (part) {
      if (part.type !== 'literal') values[part.type] = parseInt(part.value, 10);
    });
    return values;
  }

  function sameWallTime(parts, desired) {
    return (
      parts.year === desired.year &&
      parts.month === desired.month &&
      parts.day === desired.day &&
      parts.hour === desired.hour &&
      parts.minute === desired.minute
    );
  }

  function parsePacificDateTime(value) {
    var match = /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})$/.exec(value);
    if (!match) return null;

    var desired = {
      year: parseInt(match[1], 10),
      month: parseInt(match[2], 10),
      day: parseInt(match[3], 10),
      hour: parseInt(match[4], 10),
      minute: parseInt(match[5], 10),
    };
    if (
      desired.month < 1 ||
      desired.month > 12 ||
      desired.day < 1 ||
      desired.day > 31 ||
      desired.hour > 23 ||
      desired.minute > 59
    ) {
      return null;
    }

    var wallTimeAsUtc = Date.UTC(
      desired.year,
      desired.month - 1,
      desired.day,
      desired.hour,
      desired.minute
    );
    var wallTimeCheck = new Date(wallTimeAsUtc);
    if (
      wallTimeCheck.getUTCFullYear() !== desired.year ||
      wallTimeCheck.getUTCMonth() + 1 !== desired.month ||
      wallTimeCheck.getUTCDate() !== desired.day
    ) {
      return null;
    }

    // Iteratively move the UTC guess until formatting it in Pacific time gives
    // the requested wall-clock value. The final check rejects nonexistent DST
    // times (for example, 02:30 during the spring-forward gap).
    var timestamp = wallTimeAsUtc;
    for (var i = 0; i < 4; i += 1) {
      var represented = getPacificParts(timestamp);
      var representedAsUtc = Date.UTC(
        represented.year,
        represented.month - 1,
        represented.day,
        represented.hour,
        represented.minute
      );
      var adjustment = wallTimeAsUtc - representedAsUtc;
      if (adjustment === 0) break;
      timestamp += adjustment;
    }

    return sameWallTime(getPacificParts(timestamp), desired) ? timestamp : null;
  }

  window.theme.pacificTime = window.theme.pacificTime || {};
  window.theme.pacificTime.parseDateTime = parsePacificDateTime;

  if (settings.isB2bCustomer) return;

  function getScheduleState(now) {
    var startTimestamp = startAt ? parsePacificDateTime(startAt) : null;
    var endTimestamp = endAt ? parsePacificDateTime(endAt) : null;

    // Configured but malformed values fail closed and show regular pricing.
    if ((startAt && startTimestamp === null) || (endAt && endTimestamp === null)) {
      return { active: false, nextBoundary: null };
    }
    if (startTimestamp !== null && now < startTimestamp) {
      return { active: false, nextBoundary: startTimestamp };
    }
    if (endTimestamp !== null && now >= endTimestamp) {
      return { active: false, nextBoundary: null };
    }

    return { active: true, nextBoundary: endTimestamp };
  }

  function setHidden(element, hidden) {
    element.hidden = hidden;
    element.classList.toggle('hidden', hidden);
  }

  function applySchedule(scope, active) {
    var root = scope || document;
    root.querySelectorAll('[data-site-discount-promotion]').forEach(function (element) {
      setHidden(element, !active);
    });
    root.querySelectorAll('[data-site-discount-regular]').forEach(function (element) {
      setHidden(element, active);
    });
  }

  function refreshSchedule(scope) {
    var now = Date.now();
    var state = getScheduleState(now);
    applySchedule(scope || document, state.active);

    if (scope) return state;
    if (boundaryTimer) window.clearTimeout(boundaryTimer);
    boundaryTimer = null;
    if (state.nextBoundary !== null) {
      var delay = Math.min(Math.max(state.nextBoundary - now + 100, 0), maxTimerDelay);
      boundaryTimer = window.setTimeout(function () {
        refreshSchedule();
      }, delay);
    }
    return state;
  }

  function formatMoney(cents) {
    if (
      window.Shopify &&
      typeof window.Shopify.formatMoney === 'function' &&
      window.theme.moneyFormat
    ) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function decorate(scope, variant) {
    if (!scope || !variant || variant.price == null) return;
    var blocks = scope.querySelectorAll('[data-discount-price-block]');
    var regularBlocks = scope.querySelectorAll('[data-site-discount-regular]');
    var originalCents = variant.price;

    blocks.forEach(function (block) {
      var pct = parseFloat(block.getAttribute('data-site-discount-percentage')) || defaultPct;
      var steps = (block.getAttribute('data-site-discount-steps') || String(pct)).split('+');
      var priceEl = block.querySelector('[data-product-price]');
      var compareEl = block.querySelector('[data-compare-price]');
      var compareTextEl = block.querySelector('[data-compare-text]');
      if (!priceEl || pct <= 0 || pct >= 100) return;

      // Shopify floors each discount allocation to a whole cent before
      // applying the next sequential discount.
      var discountedCents = originalCents;
      steps.forEach(function (step) {
        var stepPct = parseFloat(step) || 0;
        discountedCents -= Math.floor((discountedCents * stepPct) / 100);
      });

      priceEl.innerHTML = formatMoney(discountedCents);
      priceEl.classList.remove('product__price--sale');

      if (compareEl) {
        compareEl.innerHTML = formatMoney(originalCents);
        compareEl.classList.remove('hidden');
      }
      if (compareTextEl) compareTextEl.classList.remove('hidden');
    });

    regularBlocks.forEach(function (block) {
      var priceEl = block.querySelector('[data-site-discount-regular-price]');
      var compareEl = block.querySelector('[data-site-discount-regular-compare]');
      var compareCents = variant.compare_at_price || 0;
      var onSale = compareCents > originalCents;
      if (priceEl) priceEl.innerHTML = formatMoney(originalCents);
      block.classList.toggle('sale', onSale);
      if (compareEl) {
        compareEl.innerHTML = onSale ? formatMoney(compareCents) : '';
        setHidden(compareEl, !onSale);
      }
    });
  }

  document.addEventListener(
    'theme:variant:change',
    function (event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;
      decorate(event.target, variant);
      refreshSchedule(event.target);
    },
    true
  );

  document.addEventListener('shopify:section:load', function (event) {
    refreshSchedule(event.target);
  });

  if (window.MutationObserver && document.documentElement) {
    new MutationObserver(function (mutations) {
      var hasAddedPricing = mutations.some(function (mutation) {
        return Array.prototype.some.call(mutation.addedNodes, function (node) {
          if (!node.querySelector && !node.matches) return false;
          return (
            (node.matches && node.matches('[data-site-discount-promotion], [data-site-discount-regular]')) ||
            (node.querySelector && node.querySelector('[data-site-discount-promotion], [data-site-discount-regular]'))
          );
        });
      });
      if (!hasAddedPricing || mutationRefreshQueued) return;
      mutationRefreshQueued = true;
      window.requestAnimationFrame(function () {
        mutationRefreshQueued = false;
        refreshSchedule();
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  window.theme.siteDiscountPricing = {
    refresh: refreshSchedule,
    isActiveAt: function (timestamp) {
      return getScheduleState(timestamp == null ? Date.now() : timestamp).active;
    },
    parsePacificDateTime: parsePacificDateTime,
  };

  refreshSchedule();
})();
